import React, { useId } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const spinnerVariants = cva("rounded-full motion-safe:animate-spin-slow", {
  variants: {
    variant: {
      accent: "text-icon-accent",
    },
  },
  defaultVariants: {
    variant: "accent",
  },
});

const sizeMap = {
  sm: "16",
  md: "24",
  lg: "32",
};

// aria-label defaults to "Loading" even if a caller passes aria-hidden — pass aria-label="" alongside
// aria-hidden to also clear it, since aria-hidden already removes both from the accessibility tree.
interface RemiSpinnerProps extends React.SVGAttributes<SVGSVGElement>, VariantProps<typeof spinnerVariants> {
  size?: keyof typeof sizeMap;
}

const RemiSpinner = ({
  ref,
  className,
  variant = "accent",
  size = "sm",
  role = "status",
  "aria-label": ariaLabel = "Loading",
  ...props
}: RemiSpinnerProps & { ref?: React.Ref<SVGSVGElement> }) => {
  const gradientId = useId();

  return (
    <svg
      width={sizeMap[size]}
      height={sizeMap[size]}
      viewBox="0 0 800 800"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(spinnerVariants({ variant }), className)}
      ref={ref}
      role={role}
      aria-label={ariaLabel}
      {...props}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
      </defs>
      <circle
        cx="400"
        cy="400"
        fill="none"
        r="300"
        strokeWidth="75"
        stroke={`url(#${gradientId})`}
        strokeLinecap="round"
        strokeDashoffset="0"
      />
    </svg>
  );
};
RemiSpinner.displayName = "RemiSpinner";

export { RemiSpinner };
